import type { RemoteServiceResponse } from '@/netadapters/EndpointAdapter'
import type { IFileTransmissionProgressListener } from '@core/FileTransmissionProgressListener'
import type { HttpRequestCancelToken } from '../HttpRequestCancelToken'
import type { RequestConfig } from '@/netadapters/AbsNetAdapter'
import type { AxiosResponse } from 'axios'

// dev only, Deprecated
export enum ChannelFixType {
  AutoSwitch,
  LanOnly,
  WanOnly,
}

export enum AvailableType {
  REACHABLE,
  REACHABLE_ERROR_CERTIFICATION,
  UNREACHABLE,
  UNINITIALIZED,
}

interface IDeviceMetaInfo {
  get id(): string

  get model(): string

  get version(): string

  accessToken?: string
}

export interface IDeviceBasicInfo extends IDeviceMetaInfo {
  get type(): DeviceType

  get connectionState(): ConnectionState
}

export interface IBaseDevice extends IDeviceMetaInfo {
  get channel(): ChannelDetail[] | undefined
}

export interface IDevice extends IDeviceBasicInfo {
  isSupportZip?: boolean

  isDisabledTransmissionFile: boolean
  /**
   * Get Device hash code. //TODO: public just for test
   * @returns
   */
  hashCode(): string
  /**
   * Equal Device hash code with the other device hash code.
   * @returns
   */
  equals(other: IDevice): boolean
  /**
   * Get all channelInfo in device.
   * Only allowed to be set by {@link DeviceManager}
   * @internal
   * @returns all channelInfo in device
   */
  getAllChannelInfo(): ChannelDetail[]

  /**
   * Only allowed to be set by {@link DeviceManager}
   * Update the device channel available by channel detail.
   * @internal
   */
  updateChannelAvailability(detail: ChannelDetail): boolean

  /**
   * Only allowed to be set by {@link DeviceManager}
   * Only channel host is equal,copy the device channel available by device.
   * @internal
   */
  copyChannelAvailabilityWithSameHost(Device: IDevice): void

  /**
   * Set the device access token.
   * Only allowed to be set by {@link DeviceManager}
   * @internal
   * Only allowed to be used by {@link DeviceManager}
   */
  updateAccessToken(updateToken?: string | undefined): Promise<void>

  canCallService(): boolean

  /**
   * Call remote service by service ID.
   *
   * @param id: the identify of the service in string.
   * @param data the payload of the service invoking
   * @param config the request configuration {@link RequestConfig}, including timeout settings, etc.
   * @param requestCancelToken used to cancel request
   */
  callService(
    id: string,
    data: unknown,
    config: RequestConfig,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<AxiosResponse<RemoteServiceResponse> | undefined>

  /**
   * @deprecated
   * Use the given channel type as channel choosing strategy. Temp added for integration & debug convenience.
   */
  fixChannel(channel: ChannelFixType): void

  /**
   * 上传指定文件到端侧
   *
   * @param targetPath 要存放在手机端的地址
   * @param file 文件对象{@link File}
   * @param progressListener {@link IFileTransmissionProgressListener} will be called when transmission progress changed
   * @param requestCancelToken used to cancel request
   */

  uploadFile(
    targetPath: string,
    file: File,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken,
    fileMd5?: string
  ): Promise<RemoteServiceResponse | undefined>

  /**
   * 获取下载文件地址
   * **注意**：由于设备的连接信息是变动的，如果在业务代码中一次获取地址后就一直使用，可能会导致连接因为设备的连接信息变动而不可用。
   *
   * @param targetPath 文件路径
   * @param forceRefreshToken 可选参数，默认值为false。强制刷新access token，如果获取到的链接在下载时收到的响应为HTTP status 200，并且消息体中 statusCode为401，
   * 则说明token过期，需要重新调用该接口并且设置{@link forceRefreshToken} 为 ture， 此时将重新刷新access token，并返回带新的token的地址
   * @param useLan 可选参数, 获取局域网的地址时为true，否者获取的公网地址。默认是false
   * @return 用于文件下载的URL地址
   *
   * @throws {@link NoChannelAvailableException}
   * 获取下载地址错误时抛出异常
   */
  getDownloadUrl(targetPath: string, forceRefreshToken?: boolean, useLan?: boolean): Promise<string | undefined>

  /**
   * 刷新一次probe公网探测从而获取当前设备公网是否可以建立有效链接
   * @param requestCancelToken used to cancel request
   */
  getWanAvailableInfo(requestCancelToken?: HttpRequestCancelToken): Promise<AvailableType>

  /**
   * 获取当前设备局域网是否可以建立有效链接
   * 有效链接：局域网互通 & 存在https证书 & 证书未过期
   * @param forceRefresh 是否需要刷新一次probe从而获取最新的连接情况
   * @param requestCancelToken used to cancel request
   */
  getLanAvailableInfo(forceRefresh?: boolean, requestCancelToken?: HttpRequestCancelToken): Promise<AvailableType>
}

export const enum ConnectionState {
  UNKNOWN = -1, //未初始化
  CONNECTED = 0, // 已连接
  UNCONNECTED = 1, //未连接
}

export const enum DeviceType {
  PHONE,
  PAD,
  TV,
  WATCH,
}

export interface ChannelInfo {
  host: string
}

export enum ChannelType {
  LAN,
  WAN,
}

export interface ChannelDetail extends ChannelInfo {
  type: ChannelType
  available: AvailableType
}
